// ./src/components/features/DisplayLayouts/LayoutFormModal/LayoutFormModal.tsx
import React, { useState, useEffect } from "react";
import {
  Grid,
  Stack,
  Alert,
  Text,
  ScrollArea,
  Box,
  TextInput,
  Textarea,
} from "@mantine/core";
import { AppModal, AppIcon } from "@/components/generic";
import { defaultLayoutConfig, layoutEditorUiConfig } from "@/config";
import { ThemeSectionsList } from "@/components/features/Themes/ThemeFormModal/ThemeSectionsList";
import {
  ThemeSectionEditor,
  ThemeSectionConfig,
} from "@/components/features/Themes/ThemeFormModal/ThemeSectionEditor";
import {
  parseLayoutConfig,
  setNestedValue,
  stringifyLayoutConfig,
} from "./layoutConfigUtils";
import { DisplayLayout, DisplayLayoutInput } from "@/utils";

export interface LayoutFormModalProps {
  /** Whether the modal is open */
  opened: boolean;

  /** Function to close the modal */
  onClose: () => void;

  /** Layout to edit (if null/undefined, it's create mode) */
  layout?: DisplayLayout | null | undefined;

  /** Callback when layout should be created */
  onCreateLayout?: (
    layoutData: DisplayLayoutInput
  ) => Promise<DisplayLayout | null>;

  /** Callback when layout should be updated */
  onUpdateLayout?: (
    layoutId: string,
    layoutData: DisplayLayoutInput
  ) => Promise<DisplayLayout | null>;

  /** Whether create operation is in progress */
  isCreating?: boolean;

  /** Whether update operation is in progress */
  isUpdating?: boolean;

  /** Last error from layout operations */
  lastError?: string | null;

  /** Callback to clear errors */
  onClearError?: () => void;
}

interface LayoutFormData {
  name: string;
  description: string | null | undefined;
  layoutConfigData: Record<string, any>;
  status: number;
}

const initialFormData: LayoutFormData = {
  name: "",
  description: null,
  layoutConfigData: {},
  status: 100,
};

export const LayoutFormModal: React.FC<LayoutFormModalProps> = ({
  opened,
  onClose,
  layout,
  onCreateLayout,
  onUpdateLayout,
  isCreating = false,
  isUpdating = false,
  lastError,
  onClearError,
}) => {
  const [formData, setFormData] = useState<LayoutFormData>(initialFormData);
  const [selectedSectionKey, setSelectedSectionKey] =
    useState<string>("header");
  const [validationErrors, setValidationErrors] = useState<any>({});

  const isEditMode = Boolean(layout) && Boolean(layout?.id);
  const isSubmitting = isCreating || isUpdating;

  // Reset form when modal opens/closes or layout changes
  useEffect(() => {
    if (opened) {
      if (layout) {
        // Edit mode - populate form with layout data (create a copy)
        const parsedConfig = parseLayoutConfig(layout.layoutConfig);
        setFormData({
          name: layout.name || "",
          description: layout.description || null,
          layoutConfigData: Object.assign(
            {},
            defaultLayoutConfig,
            parsedConfig
          ),
          status: layout.status != null ? layout.status : 100,
        });
      } else {
        // Create mode - use defaults
        const parsedConfig = parseLayoutConfig(null);

        setFormData({
          name: "",
          description: null,
          layoutConfigData: parsedConfig,
          status: 100,
        });
      }

      // Reset to first section
      setSelectedSectionKey("header");
      setValidationErrors({});
      onClearError?.();
    }
  }, [opened, layout, onClearError]);

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: any = {};

    // Required fields
    if (!formData.name.trim()) {
      newErrors.name = "Layout name is required";
    }

    setValidationErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle layout name change
  const handleNameChange = (value: string) => {
    setFormData((prev) => ({
      ...prev,
      name: value,
    }));

    // Clear validation error when user starts typing
    if (validationErrors.name) {
      setValidationErrors((prev: any) => ({
        ...prev,
        name: undefined,
      }));
    }
  };

  const handleLayoutFieldChange = (fieldName: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]: value,
    }));

    // Clear validation error when user starts typing
    if (validationErrors.name) {
      setValidationErrors((prev: any) => ({
        ...prev,
        [fieldName]: undefined,
      }));
    }
  };

  // Handle config change for any element
  const handleConfigChange = (path: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      layoutConfigData: setNestedValue(prev.layoutConfigData, path, value),
    }));
  };

  // Handle save
  const handleSubmit = async () => {
    onClearError?.();

    if (!validateForm()) {
      return;
    }

    try {
      // Stringify config
      const layoutConfigString = stringifyLayoutConfig(
        formData.layoutConfigData
      );

      if (!layoutConfigString) {
        console.error("Failed to stringify layout config");
        return;
      }

      // Prepare layout data
      const layoutData: DisplayLayoutInput = {
        name: formData.name.trim(),
        description: formData.description?.trim(),
        layoutConfig: layoutConfigString,
        status: formData.status,
      };

      console.log("Submit layout:", layoutData);

      let result: DisplayLayout | null = null;

      if (isEditMode && layout && onUpdateLayout) {
        // Update existing layout
        result = await onUpdateLayout(layout.id || "", layoutData);
      } else if (!isEditMode && onCreateLayout) {
        // Create new layout
        result = await onCreateLayout(layoutData);
      }

      // If successful, close the modal
      if (result) {
        onClose();
      }
    } catch (error) {
      console.error("Form submission error:", error);
    }
  };

  const handleCancel = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  // Find the selected section config
  const selectedSection = layoutEditorUiConfig.find(
    (section) => section.sectionKey === selectedSectionKey
  );

  // Get the error to display for a field
  const getDisplayError = (field: string): string | undefined => {
    return validationErrors[field];
  };

  // Render generic errors
  const renderGenericErrors = () => {
    if (!lastError) {
      return null;
    }

    return (
      <Alert
        color="red"
        icon={<AppIcon iconName="AlertCircle" size={16} />}
        title="Error"
      >
        <Text size="sm">{lastError}</Text>
      </Alert>
    );
  };

  return (
    <AppModal
      opened={opened}
      onClose={handleCancel}
      title={isEditMode ? "Edit Layout" : "Create New Layout"}
      size="xl"
      loading={isSubmitting}
      actions={[
        {
          label: "Cancel",
          variant: "secondary",
          onClick: handleCancel,
          disabled: isSubmitting,
        },
        {
          label: "Save",
          variant: "primary",
          iconName: "Check",
          onClick: handleSubmit,
          inProgress: isSubmitting,
        },
      ]}
    >
      <Stack gap="md">
        {/* Layout Name Input */}
        <TextInput
          label="Layout Name"
          placeholder="Enter layout name"
          value={formData.name}
          onChange={(e) => handleNameChange(e.target.value)}
          error={getDisplayError("name")}
          required
          withAsterisk
          size="md"
        />

        <Textarea
          label="Description"
          placeholder="Enter layout description"
          value={formData.description || ""}
          onChange={(e) =>
            handleLayoutFieldChange("description", e.target.value)
          }
          error={getDisplayError("description")}
          rows={2}
          size="md"
        />

        {/* Two-column layout: sections list on left, editor on right */}
        <Grid gutter="md">
          {/* Left column: Sections list */}
          <Grid.Col span={{ base: 12, sm: 4, md: 4 }}>
            <Box
              style={(theme) => ({
                borderRight: `1px solid ${theme.colors.gray[3]}`,
                paddingRight: theme.spacing.sm,
                height: "60vh",
                overflowY: "auto",
              })}
            >
              <ThemeSectionsList
                sections={layoutEditorUiConfig as ThemeSectionConfig[]}
                selectedSectionKey={selectedSectionKey}
                onSelectSection={setSelectedSectionKey}
              />
            </Box>
          </Grid.Col>

          {/* Right column: Section editor */}
          <Grid.Col span={{ base: 12, sm: 8, md: 8 }}>
            <ScrollArea h="60vh" type="auto">
              <Box pr="md">
                {selectedSection ? (
                  <ThemeSectionEditor
                    sectionConfig={selectedSection as ThemeSectionConfig}
                    themeConfigData={formData.layoutConfigData}
                    onConfigChange={handleConfigChange}
                  />
                ) : (
                  <Box p="md">
                    <Text size="sm" c="dimmed">
                      Select a section to configure
                    </Text>
                  </Box>
                )}
              </Box>
            </ScrollArea>
          </Grid.Col>
        </Grid>

        {/* Error display */}
        {renderGenericErrors()}
      </Stack>
    </AppModal>
  );
};
